import { HttpClient, HttpParams, } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { CacheService, CLOSE_PERIOD_TYPES, AuthService, ROLES, IClosePeriodBO } from '@mapfre-tech/amap3';
import moment from "moment";
import { Observable } from "rxjs";
import { map, tap } from "rxjs/operators";
import { environment } from "../../../environments/environment";

/** Cierre de periodo tal y como lo consume la UI: `exercise` como `{ year }` y fecha normalizada. */
export type ClosePeriodView = Omit<IClosePeriodBO, 'exercise'> & { exercise: { year: number } };

/** Campos necesarios para evaluar cierres; valido tanto para `IClosePeriodBO` como para `ClosePeriodView`. */
type ClosePeriodDates = Pick<IClosePeriodBO, 'typeClosePeriod' | 'mrkActive' | 'dateFinish'>;

export function mapClosePeriod(closePeriod: IClosePeriodBO): ClosePeriodView {
   const exercise = closePeriod.exercise as number | { year: number } | undefined;
   return {
      ...closePeriod,
      exercise: typeof exercise === 'object' && exercise !== null ? exercise : { year: exercise as number },
      dateFinish: moment(closePeriod.dateFinish, "YYYY-MM-DD").format("YYYY-MM-DD"),
      mrkActive: closePeriod.mrkActive ?? false
   };
}

@Injectable({
   providedIn: "root"
})
export class ClosePeriodService {
   constructor(private http: HttpClient, private cacheService: CacheService, private authService: AuthService) { }

   className = 'ClosePeriodService';

   getAllClosePeriods(cache = true, cacheKey = "allClosePeriods"): Observable<ClosePeriodView[]> {
      if (this.cacheService.canUseCache(cacheKey) && cache) {
         return this.cacheService.getCacheData(cacheKey) as object as Observable<ClosePeriodView[]>;
       } else {
         const observable = this.http.get<IClosePeriodBO[]>(environment.base_URL + "/close-periods").pipe(
            map(res => {
               const closePeriods = res.map(mapClosePeriod);
               this.cacheService.updateCacheData(closePeriods, cacheKey);
               return closePeriods;
         }))
         this.cacheService.updateCacheReq(cacheKey);
         return observable;
       }
   }

   getClosePeriodsByExercise(exercise: any, cache = true, cacheKey = "closePeriodsExercise"): Observable<ClosePeriodView[]> {
      const params = new HttpParams().set('exercise', exercise);
      if (cache && this.cacheService.canUseCache(cacheKey, [exercise.toString()])) {
         return this.cacheService.getCacheData(cacheKey, [exercise.toString()]) as object as Observable<ClosePeriodView[]>;
       } else {
         const observable = this.http.get<IClosePeriodBO[]>(environment.base_URL + "/close-periods", { params: params }).pipe(
            map(res => {
               const closePeriods = res.filter(it => it.mrkActive).map(mapClosePeriod);
               this.cacheService.updateCacheData(closePeriods, cacheKey, [exercise.toString()]);
               return closePeriods;
            })
         );
         this.cacheService.updateCacheReq(cacheKey, [exercise.toString()]);
         return observable;
      }
   }

   newClosePeriod(newClosePeriod: IClosePeriodBO) {
      return this.http.post(environment.base_URL + "/close-periods", newClosePeriod).pipe(tap(() => this.deleteCache()));
   }

   updateClosePeriod(editedClosePeriod: IClosePeriodBO, closePeriodId: number) {
      return this.http
         .put(environment.base_URL + `/close-periods/${closePeriodId}`, editedClosePeriod).pipe(tap(() => this.deleteCache()));
   }

   deleteCache() {
      ["allClosePeriods", "closePeriodsExercise"].forEach(key => this.cacheService.deleteCache(key));
   }

   /**
    * Devuelve si a determinado modelo le aplica cierre en base a la fecha de cierre.
    * Si hay un cierre para el año en cuestion, si la fecha del modelo es anterior a la fecha de cierre no se permitira
    * 
    * @param date 
    * @param closeDate 
    * @returns boolean false si no aplica cierre, true si aplica cierre
    * 
    * @throws Error si el tipo de las fechas es incorrecto
    */
   isBeforeCloseDate(date: moment.MomentInput, closeDate: moment.MomentInput, isAdmin = false) {
      if (isAdmin) return false;

      const previousDateMoment = moment(date);
      const closeDateMoment = moment(closeDate);

      if (!previousDateMoment.isValid() || !closeDateMoment.isValid()) {
         throw new Error('Fecha inválida proporcionada');
      }

      return previousDateMoment.isSameOrBefore(closeDateMoment)
   }

   isClosed(modelDate: moment.MomentInput, isAdmin = false, closePeriods: ClosePeriodDates[], secondaryCloseTypePk?: number): boolean{
      let isClosed = false;
      const principalClosed = closePeriods.find(
         item => item.typeClosePeriod?.idTypeClosePeriodPk === CLOSE_PERIOD_TYPES.CIERRE_PRINCIPAL && item.mrkActive
      );
      const secondaryClose = closePeriods.find(
         item => item.typeClosePeriod?.idTypeClosePeriodPk === secondaryCloseTypePk && item.mrkActive
      );

      if (principalClosed) {
         isClosed = this.isBeforeCloseDate(modelDate, principalClosed.dateFinish, isAdmin)
         if (isClosed) return isClosed; 
      }
      if (secondaryClose) {
         isClosed = this.isBeforeCloseDate(modelDate, secondaryClose.dateFinish, isAdmin)
         if (isClosed) return isClosed; 
      }
      return isClosed;
   }

   isClosedAsync(modelDate: number, isAdmin = false, secondaryCloseTypePk?: number): Observable<boolean> {
      return this.getClosePeriodsByExercise(modelDate, true, "closePeriodsExercise").pipe(
         map((closePeriods: ClosePeriodView[]) => {
            let isClosed = false;
            const principalClosed = closePeriods.find(
               item => item.typeClosePeriod?.idTypeClosePeriodPk === CLOSE_PERIOD_TYPES.CIERRE_PRINCIPAL && item.mrkActive
            );
            const secondaryClose = closePeriods.find(
               item => item.typeClosePeriod?.idTypeClosePeriodPk === secondaryCloseTypePk && item.mrkActive
            );

            if (principalClosed) {
               isClosed = this.isBeforeCloseDate(modelDate, principalClosed.dateFinish, isAdmin);
               if (isClosed) return isClosed;
            }
            if (secondaryClose) {
               isClosed = this.isBeforeCloseDate(modelDate, secondaryClose.dateFinish, isAdmin);
               if (isClosed) return isClosed;
            }
            return isClosed;
         })
      );
   }

   getCloseDate(closePeriods: ClosePeriodDates[], secondaryCloseTypePk?: number): moment.MomentInput{
      const principalClose = closePeriods.find(
         item => item.typeClosePeriod?.idTypeClosePeriodPk === CLOSE_PERIOD_TYPES.CIERRE_PRINCIPAL && item.mrkActive
      );
      const secondaryClose = closePeriods.find(
         item => item.typeClosePeriod?.idTypeClosePeriodPk === secondaryCloseTypePk && item.mrkActive
      );

      if (!principalClose && !secondaryClose) {
         return null
      }

      if (!principalClose ){
         return secondaryClose?.dateFinish
      } 

      if (!secondaryClose) {
         return principalClose?.dateFinish
      }
         
      return moment(principalClose.dateFinish).isAfter(moment(secondaryClose.dateFinish)) ? principalClose.dateFinish : secondaryClose.dateFinish;
   }

   hasEditingPermission(): boolean {
     return this.authService.userHasAtleastOneRole([ROLES.ADMINISTRADOR, ROLES.DIRECTOR, ROLES.SUBDIRECTOR])
   }

}
